"use client"

import { Eye, Edit3, TrendingUp, MoreHorizontal, Copy, Trash } from "lucide-react"
import { Button } from "../ui/button"
import { Card, CardContent } from "../ui/card"
import { useState, useRef, useEffect } from "react"
import { dictionary } from "../../utils/dictionary"
import { useAuth } from "../../context/AuthContext"
import { decodeTokenService } from "../../services/decodeTokenService"
import { toast } from "react-hot-toast"
import { deleteHomeById, postDuplicateHome } from "../../services/home/homeService"

export interface HomesData {
	id: string
	owner_id: string
	subdomain: string
	funnels: string[]
	context: string
	configurations: Record<string, any>
	template_design: Record<string, any>
	brand_name: string
	created_at: string
	updated_at: string
}

export interface HomesCardProps {
	data: HomesData
}

const getStoreDomain = () =>
	import.meta.env.VITE_DOMAIN_STORES ||
	import.meta.env.VITE_DOMIAN_STORES ||
	(import.meta.env.DEV ? "dev.storesk.com" : "storesk.com");

const openExternalUrl = (url: string) => {
	window.open(url, "_blank", "noopener,noreferrer");
};

const buildStorePreviewUrl = (subdomain: string) => {
	const trimmedSubdomain = subdomain.trim();

	if (/^https?:\/\//i.test(trimmedSubdomain)) {
		return trimmedSubdomain;
	}

	const domain = getStoreDomain();
	const protocol = domain.includes("localhost") ? "http" : "https";

	return `${protocol}://${trimmedSubdomain}.${domain}`;
};

export const HomesCard: React.FC<HomesCardProps> = ({ data }) => {
	const { language } = useAuth();
	const [isMenuOpen, setIsMenuOpen] = useState(false)
	const menuRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
				setIsMenuOpen(false)
			}
		}

		document.addEventListener("mousedown", handleClickOutside)
		return () => document.removeEventListener("mousedown", handleClickOutside)
	}, [])

	const handleMenuItemClick = (action: () => void) => {
		if (action) action()
		setIsMenuOpen(false)
	};

	const handleDuplicate = (websiteId: string) => {
		postDuplicateHome(websiteId)
			.then((res) => {
				toast.success("Tienda duplicada correctamente")
			})
			.catch((err) => {
				toast.error("Error al duplicar la tienda")
			})
			.finally(() => {
				window.location.reload()
			})
	}

	const handleDelete = (websiteId: string) => {
		deleteHomeById(websiteId)
			.then((res) => {
				toast.success("Tienda eliminada correctamente")
			})
			.catch((err) => {
				toast.error("Error al eliminar la tienda")
			})
			.finally(() => {
				window.location.reload()
			})
	}

	const handleEdit = (websiteId: string) => {
		const userId = decodeTokenService.getUserIdFromToken() || "";
		const token = localStorage.getItem("access_token") || "";
		const url =
			import.meta.env.VITE_REACT_APP_URL_BUILDER +
			`builder/store/${userId}/${websiteId}?token=${token}`;
		openExternalUrl(url);
	}

	const handleView = (store: HomesData) => {
		if (store.subdomain?.trim()) {
			openExternalUrl(buildStorePreviewUrl(store.subdomain));
			return;
		}

		const userId = decodeTokenService.getUserIdFromToken() || "";
		const url =
			import.meta.env.VITE_REACT_APP_URL_BUILDER +
			`user/${userId}/store/${store.id}`;
		openExternalUrl(url);
	}

	const handleStatistics = (websiteId: string) => {
	}

	return (
		<Card className="relative w-full bg-white border border-gray-200 hover:shadow-md transition-shadow rounded-3xl">
			{/* Menú dropdown personalizado */}
			<div className="absolute right-[8px] top-[10px]" ref={menuRef}>
				<Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setIsMenuOpen(!isMenuOpen)}>
					<MoreHorizontal className="h-4 w-4 text-gray-400" />
				</Button>

				{isMenuOpen && (
					<div className="absolute right-0 top-8 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-10">
						<button
							className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
							onClick={() => handleMenuItemClick(() => { handleDuplicate(data.id) })}
						>
							<Copy className="h-4 w-4" />
							{dictionary["duplicate"][language]}
						</button>
						<button
							className="w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-100 flex items-center gap-2"
							onClick={() => handleMenuItemClick(() => { handleDelete(data.id) })}
						>
							<Trash className="h-4 w-4" />
							{dictionary["delete"][language]}
						</button>
					</div>
				)}
			</div>
			<CardContent className="p-6">
				{/* Header con badge y menú */}
				<div className="flex justify-between">
					<div className="flex flex-col justify-between">
						<div className="flex items-start justify-between mb-2">
							<div className="flex items-center gap-2">
								<div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
									<div className="w-2 h-2 bg-green-500 rounded-full"></div>
									{dictionary["active"]?.[language] ?? "Activo"}
								</div>
							</div>
						</div>
						{/* Título principal */}
						<div className="mb-4">
							<h3 className="text-xl font-bold text-gray-900 leading-tight mb-3">{data.brand_name}</h3>
							<hr className="border-gray-200" />
						</div>
						<div>
							<p className="text-gray-600 text-sm">{data?.funnels?.length || 0}  {dictionary["connected-funnels"][language]}</p>
						</div>
					</div>
					{/* Información de embudos y botones de acción */}
					<div className="flex flex-col items-center justify-center">
						<div className="flex items-center gap-2">
							<Button
								variant="ghost"
								size="sm"
								className="h-10 w-10 p-0 rounded-full bg-gray-100 hover:bg-gray-200"
								onClick={() => { handleView(data) }}
							>
								<Eye className="h-4 w-4 text-gray-600" />
							</Button>
							<Button
								variant="ghost"
								size="sm"
								className="h-10 w-10 p-0 rounded-full bg-gray-100 hover:bg-gray-200"
								onClick={() => { handleEdit(data.id) }}
							>
								<Edit3 className="h-4 w-4 text-gray-600" />
							</Button>
							<Button
								variant="ghost"
								size="sm"
								className="h-10 w-10 p-0 rounded-full bg-gray-100 hover:bg-gray-200"
								onClick={() => { handleStatistics(data.id) }}
							>
								<TrendingUp className="h-4 w-4 text-gray-600" />
							</Button>
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
	)
}
